import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import TopBar from "./order-components/update-order-components/TopBar";
import { useNavigate, useParams } from "react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { getAllTags, getSingleOrder } from "src/queries/queriess";
import Tags from "./order-components/update-order-components/tags";
import {
  Box,
  CircularProgress,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import { useFormik } from "formik";
import { motion } from "framer-motion";
import { createOrderSchema } from "src/helper/schema";
import { createOsndOrderSchema } from "src/helper/osndSchema";
import OrderForm from "./order-components/update-order-components/order-form";
import SingleOrderForm from "./order-components/update-order-components/single-order-form";
import BillingPartyUpdateModal from "./order-components/update-order-components/BillingPartyUpdateModal";
import { cn, searchParamsToString } from "src/helper/publicFunction";
import {
  changeOrderType,
  deleteOrderStop,
  deleteQouteApi,
  getAccessorials,
  getAllCustomerAddresses,
  getCustomers,
  updateOrder,
  getAccountSettings,
} from "src/queries/query";
import { toast } from "react-toastify";
import EventsTimeline from "src/components/apps/LegsComponents/Activities/ActivityComponent";
import { useQuote } from "src/contexts/QuoteContext";

const tabs = [
  {
    id: "point_to_point",
    label: "Point-to-point",
    description:
      "This order has one shipper and one consignee. For multiple shippers or consignee, select any of the multi-stop tabs above.",
    icon: (
      <div className="flex items-center mr-2">
        <div className="w-1.5 h-1.5 bg-[#1e55bf] rounded-full mr-1"></div>
        <div className="w-1.5 h-1.5 bg-[#1e55bf] rounded-full"></div>
      </div>
    ),
  },
  {
    id: "single_shipper",
    label: "Single shipper",
    description:
      "This order has a single shipper that goes to multiple consignees. For multiple shippers, select any of the multi-stop tabs above.",
    icon: (
      <div className="flex items-center mr-2">
        <svg
          className="w-5 h-5 text-gray-500"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M8 19a3 3 0 100-6 3 3 0 000 6z" fill="currentColor" />
          <path d="M16 19a3 3 0 100-6 3 3 0 000 6z" fill="currentColor" />
          <path d="M8 10a3 3 0 100-6 3 3 0 000 6z" fill="currentColor" />
          <path d="M8.5 13v-3h7v3" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      </div>
    ),
  },
  {
    id: "single_consignee",
    label: "Single consignee",
    description: "This order has multiple shippers and one consignee.",
    icon: (
      <div className="flex items-center mr-2">
        <svg
          className="w-5 h-5 text-gray-500"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M16 19a3 3 0 100-6 3 3 0 000 6z" fill="currentColor" />
          <path d="M8 19a3 3 0 100-6 3 3 0 000 6z" fill="currentColor" />
          <path d="M16 10a3 3 0 100-6 3 3 0 000 6z" fill="currentColor" />
          <path d="M15.5 13v-3h-7v3" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      </div>
    ),
  },
  {
    id: "sequence",
    label: "Sequence",
    description: "This order has multiple stops in a specific sequence.",
    icon: (
      <div className="flex items-center mr-2">
        <svg
          className="w-5 h-5 text-gray-500"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M8 6a2 2 0 100-4 2 2 0 000 4z" fill="currentColor" />
          <path d="M8 14a2 2 0 100-4 2 2 0 000 4z" fill="currentColor" />
          <path d="M8 22a2 2 0 100-4 2 2 0 000 4z" fill="currentColor" />
          <path d="M16 6a2 2 0 100-4 2 2 0 000 4z" fill="currentColor" />
          <path d="M16 14a2 2 0 100-4 2 2 0 000 4z" fill="currentColor" />
          <path d="M16 22a2 2 0 100-4 2 2 0 000 4z" fill="currentColor" />
          <path
            d="M10 4h4M10 12h4M10 20h4"
            stroke="currentColor"
            strokeWidth="1.5"
          />
        </svg>
      </div>
    ),
  },
];

const CreateOrder = ({ isOSND: isOSNDProp = false }) => {
  const params = useParams();
  const orderId = params.uuid;
  const navigate = useNavigate();

  // Determine if this is an OSND order based on URL or prop
  const isOSND = isOSNDProp || window.location.pathname.startsWith("/osnd");

  const [activeTab, setActiveTab] = useState("point_to_point");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingTab, setPendingTab] = useState(null);
  const [showBillingPartyModal, setShowBillingPartyModal] = useState(false);
  const detailsRef = useRef(null);
  const commoditiesRef = useRef(null);
  const { quoteData, setQuoteData } = useQuote();
  const scrollToSection = (ref) => {
    ref.current?.scrollIntoView({ behavior: "smooth" });
  };

  const {
    data: order,
    isLoading,
    isFetching,
    refetch: refetchOrder,
  } = useQuery({
    queryKey: ["order-id", orderId],
    queryFn: () => getSingleOrder(orderId),
    staleTime: Number.POSITIVE_INFINITY,
  });

  const orderNumber = order?.orderNumber;
  const orderType = order?.orderType;
  const status = order?.status;

  useEffect(() => {
    if (
      status &&
      status.toLowerCase() !== "new" &&
      status.toLowerCase() !== "saved"
    ) {
      navigate(`/order/${orderId}`);
    }
    if (orderType) {
      setActiveTab(orderType);
    }
  }, [orderId, status, navigate, orderType]);

  const { data: tagsData, isLoading: isLoadingTags } = useQuery({
    queryKey: ["tags"],
    queryFn: () => getAllTags(),
    staleTime: Number.POSITIVE_INFINITY,
  });

  const tags = tagsData?.data?.data;

  // Fetch company settings to default shipper to County Transport
  const { data: accountSettings } = useQuery({
    queryKey: ["account-settings"],
    queryFn: () => getAccountSettings(),
    staleTime: Number.POSITIVE_INFINITY,
  });

  // Memoize the default address to prevent recreation
  const defaultAddress = useMemo(
    () => ({
      name: "",
      quickSearch: "",
      shortCode: "",
      customerType: "",
      billingOption: "",
      requireQuote: false,
      currency: "CAD",
      isActive: true,
      addresses: {
        city: "",
        state: "",
        postal: "",
        address: "",
        address2: "",
        country: "",
        latitude: null,
        longitude: null,
      },
      notes: "",
      openingTime: "",
      closingTime: "",
      contactName: "",
      contactPhone: "",
      contactEmail: "",
      contactExt: "",
      readyTimeStart: "",
      readyTimeEnd: "",
      isAppointment: false,
    }),
    [],
  );

  // Memoize the format function to prevent recreation
  const formatApiAddressToDefault = useCallback(
    (apiAddress = {}) => {
      const addressSource =
        apiAddress?.address || apiAddress?.addresses || apiAddress;

      return {
        ...defaultAddress,
        name:
          apiAddress?.company ??
          apiAddress?.name ??
          apiAddress?.companyName ??
          "",
        contactName: apiAddress?.contactName ?? "",
        contactPhone: apiAddress?.phone ?? apiAddress?.contactPhone ?? "",
        contactEmail: apiAddress?.email ?? apiAddress?.contactEmail ?? "",
        contactExt: apiAddress?.phoneExt ?? apiAddress?.contactExt ?? "",
        closingTime:
          apiAddress?.businessHours?.close ?? apiAddress?.closingTime ?? "",
        openingTime:
          apiAddress?.businessHours?.open ?? apiAddress?.openingTime ?? "",
        addresses: {
          ...defaultAddress.addresses,
          address: addressSource?.address ?? addressSource?.address1 ?? "",
          city: addressSource?.city ?? "",
          state: addressSource?.state ?? "",
          postal: addressSource?.postal ?? "",
          address2: addressSource?.address2 ?? "",
          country: addressSource?.country ?? "",
          latitude: addressSource?.latitude ?? null,
          longitude: addressSource?.longitude ?? null,
        },
      };
    },
    [defaultAddress],
  );

  const { data: accessorialsData, isLoading: isLoadingAcc } = useQuery({
    queryKey: ["accessorials"],
    queryFn: () => getAccessorials(),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  // Memoize initial values to prevent infinite loops
  const initialValues = useMemo(() => {
    // Build company default address from account settings when available
    const companyAddressDefault = accountSettings
      ? {
          ...defaultAddress,
          name: accountSettings.companyName || "",
          quickSearch: accountSettings.companyName || "",
          contactPhone: accountSettings.phoneNumber || "",
          contactEmail: accountSettings.contactEmail || "",
          addresses: {
            ...defaultAddress.addresses,
            address: accountSettings.companyAddress1 || "",
            address2: accountSettings.companyAddress2 || "",
            city: accountSettings.companyCity || "",
            state: accountSettings.companyState || "",
            postal: accountSettings.companyPostal || "",
            country: accountSettings.companyCountry || "",
            latitude: null,
            longitude: null,
          },
        }
      : defaultAddress;

    // Default shipper address for new orders
    // Prefer customer's default shipper/consignee addresses when available,
    // otherwise fall back to the hard-coded Blessington location.
    const billingParty = order?.billingParty;
    // Prefer billingParty defaults, then fall back to the first order stop addresses if present
    const shipperDefaultApi =
      billingParty?.shipperDefaultAddress?.[0] ||
      order?.orderStops?.[0]?.shipperAddress;
    const consigneeDefaultApi =
      billingParty?.consigneeDefaultAddress?.[0] ||
      order?.orderStops?.[0]?.consigneeAddress;

    // Build explicit defaults for shipper and consignee
    let defaultShipper = null;
    let defaultConsignee = null;

    if (
      shipperDefaultApi &&
      (shipperDefaultApi.address || shipperDefaultApi.address1)
    ) {
      defaultShipper = formatApiAddressToDefault(shipperDefaultApi);
    }
    if (
      consigneeDefaultApi &&
      (consigneeDefaultApi.address || consigneeDefaultApi.address1)
    ) {
      defaultConsignee = formatApiAddressToDefault(consigneeDefaultApi);
    }

    // If no explicit shipper default, fall back to hard-coded Blessington
    if (!defaultShipper) {
      defaultShipper = {
        ...defaultAddress,
        name: "County transport",
        quickSearch: "20 Blessington Rd, Belleville, ON K0K 1V0, Canada",
        contactName: "",
        contactPhone: "",
        contactEmail: "",
        addresses: {
          ...defaultAddress.addresses,
          address: "20 Blessington Rd",
          address2: "",
          city: "Belleville",
          state: "ON",
          postal: "K0K 1V0",
          country: "Canada",
          latitude: 44.219577,
          longitude: -77.3790888,
        },
      };
    }

    // If no explicit consignee default, fall back to the generic defaultAddress
    if (!defaultConsignee) {
      defaultConsignee = defaultAddress;
    }
    if (!order) {
      return {
        shipper: defaultShipper,
        consignee: defaultConsignee,
        orderStops:
          activeTab === "point_to_point"
            ? [
                {
                  shipper: defaultShipper,
                  consignee: defaultConsignee,
                  accessorialUuids: [""],
                  billingMiles: "",
                  commodities: [
                    {
                      descriptions: "",
                      quantity: null,
                      lengthValue: null,
                      width: null,
                      height: null,
                      pieces: null,
                      lf: null,
                      weight: null,
                      class: "",
                      typeUuid: "",
                      _action: "create",
                    },
                  ],
                  customNumber: "",
                  portOfEntry: "",
                  declaredValue: 0,
                  poNumber: "",
                  refNumber: "",
                  tenderNumber: "",
                  readyTimeStart: "",
                  readyTimeEnd: "",
                  pickUpTimeStart: "",
                  pickUpTimeEnd: "",
                  deliveryTimeStart: "",
                  deliveryTimeEnd: "",
                  isAppointmentPickup: false,
                  isAppointmentConfirmPickup: false,
                  isAppointmentDelivery: false,
                  isAppointmentConfirmDelivery: false,
                },
              ]
            : [],
        billingParty: {
          name: "",
          shortCode: "",
          customerType: "",
          billingOption: "",
          requireQuote: false,
          isActive: true,
          addresses: {
            city: "",
            state: "",
            postal: "",
            address: "",
            address2: "",
            country: "",
            latitude: null,
            longitude: null,
          },
          notes: "",
          openingTime: "",
          closingTime: "",
          contactPhone: "",
          contactEmail: "",
          contactExt: "",
        },
        serviceUuids: [""],
        tagUuids: [""],
        billingMiles: "",
        customsBroker: "",
        currency: "CAD",
        shipperNotes: "",
        consigneeNotes: "",
        orderType: "",
        readyTimeStart: "",
        readyTimeEnd: "",
        requestedTimeStart: "",
        requestedTimeEnd: "",
        pickUpTimeStart: "",
        pickUpTimeEnd: "",
        deliveryTimeStart: "",
        deliveryTimeEnd: "",
        isAppointmentPickup: false,
        isAppointmentConfirmPickup: false,
        isAppointmentDelivery: false,
        isAppointmentConfirmDelivery: false,
        customNumber: "",
        portOfEntry: "",
        declaredValue: 0,
        poNumber: "",
        refNumber: "",
        tenderNumber: "",
        instructions: "",
        carrierCosts: 0,
        quotesTotal: 0,
        accessorialUuids: [""],
        commodities: [
          {
            descriptions: "",
            quantity: null,
            lengthValue: null,
            width: null,
            height: null,
            pieces: null,
            lf: null,
            weight: null,
            class: "",
            typeUuid: "",
            _action: "create",
          },
        ],
      };
    }

    // Check if shipper address is empty/incomplete
    const shipperAddress = order?.orderStops[0]?.shipperAddress;
    const hasShipperAddress =
      shipperAddress &&
      (shipperAddress.name ||
        shipperAddress.company ||
        shipperAddress.address?.address ||
        shipperAddress.addresses?.address);

    const values = {
      shipper: hasShipperAddress
        ? formatApiAddressToDefault(shipperAddress)
        : defaultShipper,
      consignee: order?.orderStops[0]?.consigneeAddress
        ? formatApiAddressToDefault(order.orderStops[0].consigneeAddress)
        : defaultConsignee,
      orderType: order?.orderType || "",
      orderStops: order
        ? order.orderStops?.map((stop) => ({
            ...stop,
            accessorialUuids:
              Array.isArray(stop.accessorialUuids) &&
              stop.accessorialUuids.length > 0
                ? stop.accessorialUuids
                : stop.accessorials?.map((accessorial) => accessorial.uuid) || [
                    "",
                  ],
            serviceUuids: stop.services?.map((service) => service.uuid) || [""],
            shipper:
              order?.orderType === "point_to_point"
                ? stop.shipperAddress &&
                  (stop.shipperAddress.name ||
                    stop.shipperAddress.company ||
                    stop.shipperAddress.address?.address ||
                    stop.shipperAddress.addresses?.address)
                  ? formatApiAddressToDefault(stop.shipperAddress)
                  : defaultShipper
                : stop.shipper
                ? formatApiAddressToDefault(
                    stop.shipper.addresses || stop.shipper.address,
                  )
                : stop.shipperAddress
                ? formatApiAddressToDefault(stop.shipperAddress)
                : defaultShipper,
            consignee:
              order?.orderType === "point_to_point"
                ? stop.consigneeAddress &&
                  (stop.consigneeAddress.name ||
                    stop.consigneeAddress.company ||
                    stop.consigneeAddress.address?.address ||
                    stop.consigneeAddress.address1)
                  ? formatApiAddressToDefault(stop.consigneeAddress)
                  : defaultConsignee
                : stop.consignee
                ? formatApiAddressToDefault(
                    stop.consignee.addresses || stop.consignee.address,
                  )
                : stop.consigneeAddress
                ? formatApiAddressToDefault(stop.consigneeAddress)
                : defaultConsignee,
            commodities:
              stop.commodities?.map((commodity) => ({
                descriptions: commodity.descriptions || "",
                quantity: commodity.quantity || null,
                lengthValue: commodity.lengthValue || null,
                _action: "update",
                width: commodity.width || null,
                height: commodity.height || null,
                pieces: commodity.pieces || null,
                lf: commodity.lf || null,
                weight: commodity.weight || null,
                dim: commodity.dim || null,
                class: commodity.class || "",
                typeUuid: commodity.type?.uuid || "",
                uuid: commodity?.uuid ?? "",
              })) ?? [],
            readyTimeStart: stop?.readyTimeStart || "",
            readyTimeEnd: stop?.readyTimeEnd || "",
            requestedTimeStart: stop?.requestedTimeStart || "",
            requestedTimeEnd: stop?.requestedTimeEnd || "",
            pickUpTimeStart: stop?.pickUpTimeStart || "",
            pickUpTimeEnd: stop?.pickUpTimeEnd || "",
            deliveryTimeStart: stop?.deliveryTimeStart || "",
            deliveryTimeEnd: stop?.deliveryTimeEnd || "",
            isAppointmentPickup: stop?.isAppointmentPickup || false,
            isAppointmentConfirmPickup:
              stop?.isAppointmentConfirmPickup || false,
            isAppointmentDelivery: stop?.isAppointmentDelivery || false,
            isAppointmentConfirmDelivery:
              stop?.isAppointmentConfirmDelivery || false,
          })) || []
        : activeTab === "point_to_point"
        ? [
            {
              uuid: "",
              type: "pickup",
              notes: "",
              openingTime: "",
              closingTime: "",
              accessorialUuids: [],
              billingMiles: null,
              commodities: [],
              customNumber: "",
              declaredValue: null,
              poNumber: "",
              refNumber: "",
              tenderNumber: "",
              portOfEntry: "",
              isAppointmentPickup: false,
              isAppointmentConfirmPickup: false,
              isAppointmentDelivery: false,
              isAppointmentConfirmDelivery: false,
              shipper: defaultShipper,
              consignee: defaultConsignee,
            },
          ]
        : [],
      billingParty: order?.billingParty ?? {
        name: "",
        shortCode: "",
        customerType: "",
        billingOption: "",
        requireQuote: false,
        isActive: true,
        addresses: {
          city: "",
          state: "",
          postal: "",
          address: "",
          address2: "",
          country: "",
          latitude: null,
          longitude: null,
        },
        notes: "",
        openingTime: "",
        closingTime: "",
        contactPhone: "",
        contactEmail: "",
        contactExt: "",
      },
      tagUuids:
        order?.tags?.length > 0 ? order.tags.map((tag) => tag.uuid) : [""],
      billingMiles: order?.billingMiles ?? "",
      customsBroker: order?.customsBroker ?? "",
      currency: order?.currency ?? "CAD",
      shipperNotes: order?.shipperNotes ?? "",
      consigneeNotes: order?.consigneeNotes ?? "",
      readyTimeStart: order?.readyTimeStart || "",
      readyTimeEnd: order?.readyTimeEnd || "",
      requestedTimeStart: order?.requestedTimeStart || "",
      requestedTimeEnd: order?.requestedTimeEnd || "",
      pickUpTimeStart: order?.pickUpTimeStart || "",
      pickUpTimeEnd: order?.pickUpTimeEnd || "",
      deliveryTimeStart: order?.deliveryTimeStart || "",
      deliveryTimeEnd: order?.deliveryTimeEnd || "",
      isAppointmentPickup: order?.isAppointmentPickup || false,
      isAppointmentConfirmPickup: order?.isAppointmentConfirmPickup || false,
      isAppointmentDelivery: order?.isAppointmentDelivery || false,
      isAppointmentConfirmDelivery:
        order?.isAppointmentConfirmDelivery || false,
      customNumber:
        order?.orderType === "point_to_point"
          ? order?.customNumber || order?.orderStops[0]?.customNumber
          : "",
      portOfEntry:
        order?.orderType === "point_to_point"
          ? order?.portOfEntry || order?.orderStops[0]?.portOfEntry
          : "",
      declaredValue:
        order?.orderType === "point_to_point"
          ? order?.declaredValue || order?.orderStops[0]?.declaredValue
          : 0,
      poNumber:
        order?.orderType === "point_to_point"
          ? order?.poNumber || order?.orderStops[0]?.poNumber
          : "",
      refNumber:
        order?.orderType === "point_to_point"
          ? order?.refNumber || order?.orderStops[0]?.refNumber
          : "",
      tenderNumber:
        order?.orderType === "point_to_point"
          ? order?.tenderNumber || order?.orderStops[0]?.tenderNumber
          : "",
      instructions: order?.instructions ?? "",
      carrierCosts: order?.carrierCosts ?? 0,
      quotesTotal: order?.quotesTotal ?? 0,
      commodities:
        order?.commodities?.length > 0
          ? order.commodities.map((commodity) => ({
              descriptions: commodity.descriptions || "",
              quantity: commodity.quantity || null,
              lengthValue: commodity.lengthValue || null,
              _action: "update",
              width: commodity.width || null,
              height: commodity.height || null,
              pieces: commodity.pieces || null,
              lf: commodity.lf || null,
              weight: commodity.weight || null,
              dim: commodity.dim || null,
              class: commodity.class || "",
              typeUuid: commodity.type?.uuid || "",
              uuid: commodity?.uuid ?? "",
            }))
          : [
              {
                descriptions: "",
                quantity: null,
                lengthValue: null,
                width: null,
                height: null,
                pieces: null,
                lf: null,
                weight: null,
                dim: null,
                class: "",
                typeUuid: "",
                _action: "create",
              },
            ],
    };

    if (quoteData) {
      const mapAddress = (quoteAddress) => ({
        ...defaultAddress,
        name: quoteAddress.name || "",
        addresses: {
          ...defaultAddress.addresses,
          address: quoteAddress.address || "",
          city: quoteAddress.city || "",
          state: quoteAddress.state || "",
          postal: quoteAddress.postal || "",
          country: quoteAddress.country || "",
          latitude: quoteAddress.latitude || null,
          longitude: quoteAddress.longitude || null,
        },
        contactName: quoteAddress.contactName || "",
        contactPhone: quoteAddress.contactPhone || "",
        contactEmail: quoteAddress.contactEmail || "",
      });

      if (quoteData.origin) {
        values.shipper = mapAddress(quoteData.origin);
      }
      if (quoteData.destination) {
        values.consignee = mapAddress(quoteData.destination);
      }

      const commodity = {
        descriptions: "",
        quantity: quoteData.quantity || null,
        lengthValue: quoteData.lengthValue || null,
        width: quoteData.width || null,
        height: quoteData.height || null,
        weight: quoteData.weight || null,
        dim: quoteData.dimWeight || null,
        pieces: null,
        lf: null,
        class: "",
        typeUuid: "",
        _action: "create",
      };

      if (values.orderStops && values.orderStops.length > 0) {
        const currentStop = values.orderStops[0];
        const accessorialUuids =
          quoteData.accessorialUuids || currentStop.accessorialUuids;

        let accessorials = currentStop.accessorials || [];
        if (quoteData.accessorialUuids && accessorialsData) {
          accessorials = accessorialsData.filter((acc) =>
            quoteData.accessorialUuids.includes(acc.uuid),
          );
        }

        values.orderStops[0] = {
          ...currentStop,
          shipper: quoteData.origin
            ? mapAddress(quoteData.origin)
            : currentStop.shipper,
          consignee: quoteData.destination
            ? mapAddress(quoteData.destination)
            : currentStop.consignee,
          accessorialUuids: accessorialUuids,
          accessorials: accessorials,
          commodities: [commodity],
        };
      }

      if (quoteData.accessorialUuids) {
        values.accessorialUuids = quoteData.accessorialUuids;
      }
    }

    return values;
  }, [
    order,
    defaultAddress,
    formatApiAddressToDefault,
    activeTab,
    quoteData,
    accessorialsData,
  ]);

  const {
    values,
    setFieldValue,
    resetForm,
    touched,
    errors,
    handleSubmit: formikHandleSubmit,
    setTouched,
    setValues,
    dirty,
  } = useFormik({
    initialValues,
    validationSchema: isOSND ? createOsndOrderSchema : createOrderSchema,
    enableReinitialize: true, // Disable automatic reinitialization
    onSubmit: (values) => {
      // Add isOSND flag to submitted values
      const submitValues = {
        ...values,
        ...(isOSND && { isOSND: true, skipQuote: true }),
      };
      updateOrderMutate(submitValues);
    },
  });

  const handleConfirmTabSwitch = () => {
    setShowConfirmModal(false);
    if (pendingTab) {
      setActiveTab(pendingTab);
      setPendingTab(null);

      const orderStopUuids = values?.orderStops
        ?.map((stop) => stop?.uuid)
        ?.filter((uuid) => !!uuid); // remove undefined/null
      if (orderStopUuids?.length > 0) {
        deleteStop({ orderStopUuids, orderUuid: orderId });
      }
      changeOrderTypeMutate({ orderId, orderType: pendingTab });
      refetchOrder();
    }
  };

  const handleCancelTabSwitch = () => {
    setShowConfirmModal(false);
    setPendingTab(null);
  };

  const { mutate: deleteStop, isPending: deleteStopPending } = useMutation({
    mutationFn: (payload) => {
      return deleteOrderStop(payload);
    },
    onSuccess: () => {
      toast.success("Stop deleted successfully!");
      resetForm();
      refetchOrder();
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Failed to delete Stop",
      );
    },
  });

  const { mutate: deleteQouteApiMutate } = useMutation({
    mutationFn: (payload) => {
      return deleteQouteApi(payload);
    },
    onSuccess: () => {
      toast.success("Quote deleted successfully!");
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Failed to delete Quote",
      );
    },
  });
  const { mutate: changeOrderTypeMutate, isPending: changeOrderTypePending } =
    useMutation({
      mutationFn: (payload) => {
        return changeOrderType(payload);
      },
      onSuccess: () => {
        toast.success("Order type changed successfully!");
        resetForm();

        const quotesToDelete = order.quotes.map((quote) => quote.uuid);
        deleteQouteApiMutate(quotesToDelete);
      },
      onError: (error) => {
        console.log("error", error);
      },
    });

  const handleTabChange = (tabId) => {
    setFieldValue("orderType", tabId);
    setPendingTab(tabId);
    setShowConfirmModal(true);
    // setActiveTab(tabId);
  };

  const handleSubmit = async () => {
    // setQuoteData(null);
    if (Object.keys(errors).length === 0) {
      const submissionValues = { ...values };

      // Process consignee business hours to ensure proper structure
      if (submissionValues.consignee) {
        const { openingTime, closingTime, ...restConsignee } =
          submissionValues.consignee;
        submissionValues.consignee = {
          ...restConsignee,
          businessHours: {
            open: openingTime || restConsignee.businessHours?.open || "",
            close: closingTime || restConsignee.businessHours?.close || "",
          },
        };
        // Remove individual time fields to avoid duplication
        delete submissionValues.consignee.openingTime;
        delete submissionValues.consignee.closingTime;
      }

      // Process orderStops consignee business hours if present
      if (Array.isArray(submissionValues.orderStops)) {
        submissionValues.orderStops = submissionValues.orderStops.map(
          (stop) => {
            if (stop.consignee) {
              const { openingTime, closingTime, ...restConsignee } =
                stop.consignee || stop.consigneeAddress;

              return {
                ...stop,
                consignee: {
                  ...restConsignee,
                  businessHours: {
                    open:
                      openingTime ||
                      stop?.consignee?.businessHours?.open ||
                      stop?.consigneeAddress?.businessHours?.open ||
                      "",
                    close:
                      closingTime ||
                      stop?.consignee?.businessHours?.close ||
                      stop?.consigneeAddress?.businessHours?.close ||
                      "",
                  },
                },
              };
            }
            return stop;
          },
        );
      }

      // Helper function to clean arrays
      const cleanArray = (arr) => arr.filter((item) => item !== "");

      // Clean up serviceUuids
      if (Array.isArray(submissionValues.serviceUuids)) {
        submissionValues.serviceUuids = cleanArray(
          submissionValues.serviceUuids,
        );
        if (submissionValues.serviceUuids.length === 0) {
          delete submissionValues.serviceUuids;
        }
      }

      // Clean up tagUuids
      if (Array.isArray(submissionValues.tagUuids)) {
        submissionValues.tagUuids = cleanArray(submissionValues.tagUuids);
        if (submissionValues.tagUuids.length === 0) {
          delete submissionValues.tagUuids;
        }
      }

      // Clean up accessorialUuids (top-level)
      if (Array.isArray(submissionValues.accessorialUuids)) {
        submissionValues.accessorialUuids = cleanArray(
          submissionValues.accessorialUuids,
        );
        if (submissionValues.accessorialUuids.length === 0) {
          delete submissionValues.accessorialUuids;
        }
      }

      // Clean up accessorialUuids for each orderStop to remove empty strings
      if (Array.isArray(submissionValues.orderStops)) {
        submissionValues.orderStops = submissionValues.orderStops.map(
          (stop) => {
            // gather from stop.accessorialUuids
            const base = Array.isArray(stop.accessorialUuids)
              ? stop.accessorialUuids.filter((id) => id && id !== "")
              : [];

            // gather from legs[].accessorialUuids
            const legsArray = Array.isArray(stop.legs) ? stop.legs : [];
            const fromLegsArrays = legsArray
              .map((leg) =>
                leg && Array.isArray(leg.accessorialUuids)
                  ? leg.accessorialUuids
                  : [],
              )
              .flat()
              .filter(Boolean);

            // gather from legs[].accessorials[].uuid
            const fromLegsObjects = legsArray
              .map((leg) =>
                Array.isArray(leg.accessorials)
                  ? leg.accessorials.map((a) => a && a.uuid)
                  : [],
              )
              .flat()
              .filter(Boolean);

            const merged = Array.from(
              new Set([...base, ...fromLegsArrays, ...fromLegsObjects]),
            );

            return {
              ...stop,
              accessorialUuids: merged,
            };
          },
        );
      }

      // Clean up commodities from legs
      if (submissionValues.orderStops) {
        submissionValues.orderStops.forEach((stop) => {
          if (stop.legs) {
            stop.legs.forEach((leg) => {
              if (Array.isArray(leg.commodities)) {
                leg.commodities = leg.commodities.filter((commodity) =>
                  Object.values(commodity).some(
                    (value) => value !== "" && value !== null,
                  ),
                );
                if (leg.commodities.length === 0) {
                  delete leg.commodities;
                }
              }
            });
          }
        });
      }

      updateOrderMutate(submissionValues);
    } else {
      const getErrorPaths = (obj, path = "") => {
        const paths = [];
        if (typeof obj === "string") {
          paths.push(path);
        } else if (Array.isArray(obj)) {
          obj.forEach((item, index) => {
            paths.push(...getErrorPaths(item, `${path}[${index}]`));
          });
        } else if (typeof obj === "object" && obj !== null) {
          Object.entries(obj).forEach(([key, value]) => {
            const newPath = path ? `${path}.${key}` : key;
            paths.push(...getErrorPaths(value, newPath));
          });
        }
        return paths;
      };
      console.log("errors", errors);

      const errorPaths = getErrorPaths(errors);
      console.log("Fields with errors:", errorPaths);

      // if (errorPaths.length > 0) {
      //   toast.error(`Please fix: ${errorPaths[0]}`);
      // }

      const touchAllFields = (obj) => {
        if (!obj) return {};
        if (Array.isArray(obj)) {
          return obj.map((item) => touchAllFields(item));
        }
        if (typeof obj === "object") {
          const result = {};
          Object.keys(obj).forEach((key) => {
            result[key] = touchAllFields(obj[key]);
          });
          return result;
        }
        return true;
      };

      const touchedFields = touchAllFields(values);
      setTouched(touchedFields);
      toast.error("Please fix the validation errors before submitting");
    }
  };
  console.log("showBillingPartyModal", showBillingPartyModal);
  const [searchParams] = useState({
    page: 1,
    limit: 100,
  });

  const { data: customersData, isLoading: isLoadingCustomers } = useQuery({
    queryKey: ["customers", searchParamsToString(searchParams)],
    queryFn: () =>
      getCustomers({
        params: searchParamsToString(searchParams),
      }),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const customers = customersData?.data;

  const { mutate: updateOrderMutate, isPending } = useMutation({
    mutationFn: (values) => updateOrder(orderId, values),
    onSuccess: () => {
      toast.success("Order updated successfully!");
      resetForm();
      // Navigate to order detail and indicate we came from create so the detail page can prompt for a spot quote
      navigate(`/order/${orderId}`, { state: { fromCreate: true } });
    },
    onError: (error) => {
      console.log("error", error);
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Failed to update Order",
      );
    },
  });

  const { data: customersAddresses, isLoading: isLoadingCustomerAddresses } =
    useQuery({
      queryKey: ["customerAddresses", order?.billingParty?.uuid],
      queryFn: () => getAllCustomerAddresses(order?.billingParty?.uuid),
      enabled: !!order?.billingParty?.uuid,
      staleTime: Number.POSITIVE_INFINITY,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
    });

  // Preselect "County Transport" as shipper when creating a new order
  // Searches the general customers list for a matching company/name and applies its address
  useEffect(() => {
    // Only for new orders and when shipper is still empty
    const isNewOrder = !order;
    const hasShipperName = Boolean(
      values?.shipper?.name ||
        values?.shipper?.company ||
        values?.shipper?.quickSearch,
    );
    if (!isNewOrder || hasShipperName) return;

    const customerList = Array.isArray(customers)
      ? customers
      : Array.isArray(customers?.data)
      ? customers.data
      : Array.isArray(customers?.items)
      ? customers.items
      : [];

    if (!customerList || customerList.length === 0) return;

    const match = customerList.find((c) => {
      const name = (
        c?.name ||
        c?.company ||
        c?.companyName ||
        ""
      ).toLowerCase();
      return name.includes("county transport");
    });

    if (!match) return;

    // Build default shipper from matched customer
    const addr = match?.addresses || match?.address || {};
    const companyDefault = {
      ...values.shipper,
      name:
        match?.name ||
        match?.company ||
        match?.companyName ||
        "County Transport",
      quickSearch:
        match?.name ||
        match?.company ||
        match?.companyName ||
        "County Transport",
      contactPhone: match?.contactPhone || match?.phone || "",
      contactEmail: match?.contactEmail || match?.email || "",
      addresses: {
        ...values.shipper?.addresses,
        address: addr?.address || addr?.address1 || "",
        address2: addr?.address2 || "",
        city: addr?.city || "",
        state: addr?.state || "",
        postal: addr?.postal || "",
        country: addr?.country || "",
        latitude: addr?.latitude ?? null,
        longitude: addr?.longitude ?? null,
      },
    };

    setFieldValue("shipper", companyDefault);
    if (Array.isArray(values?.orderStops) && values.orderStops.length > 0) {
      setFieldValue("orderStops[0].shipper", companyDefault);
    }
  }, [order, customers, values?.shipper?.name, values?.shipper?.quickSearch]);

  return (
    <Box
      sx={{
        width: "100%",
        height: "100vh",
        overflow: "auto",
        position: "relative",
      }}
    >
      <Box
        sx={{
          width: "100%",
          position: "sticky",
          top: 0,
          zIndex: 1100,
          backgroundColor: "background.paper",
        }}
      >
        <TopBar
          orderNumber={orderNumber}
          handleSubmit={handleSubmit}
          isLoading={isPending}
          status={order?.status}
          isOrderOSND={order?.isOSND}
          isOSND={isOSND}
        />
        <Tags
          tags={tags}
          values={values}
          setFieldValue={setFieldValue}
          orderTags={values?.tagUuids}
        />
        <Box
          sx={{
            width: "100%",
            mt: 0,
            bgcolor: "#f7f9fc",
            borderRadius: 0,
          }}
        >
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex">
              <button
                onClick={() => scrollToSection(detailsRef)}
                className={`py-2 px-4 text-sm font-medium border-b-2 ${"border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"}`}
              >
                Details
              </button>
              <button
                onClick={() => scrollToSection(commoditiesRef)}
                className={`py-2 px-4 text-sm font-medium border-b-2 ${"border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"}`}
              >
                Commodities (1)
              </button>
            </nav>
          </div>
        </Box>
      </Box>

      <div className="w-full flex items-start relative">
        <div className="w-[70%] rounded-lg border-0">
          <div className="relative bg-gradient-to-r from-slate-50 to-gray-50 border-b border-t-0 border-gray-200">
            <div className="flex">
              {tabs.map((tab, index) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={cn(
                    "relative flex items-center justify-center gap-2 flex-1 py-4 px-3 text-sm font-medium transition-all duration-200 ease-in-out",
                    "hover:bg-white/50 ",
                    activeTab === tab.id
                      ? "text-blue-700 bg-white shadow-sm"
                      : "text-gray-600 hover:text-gray-900",
                  )}
                >
                  {/* Active tab indicator */}
                  {activeTab === tab.id && (
                    <motion.div
                      layoutId="activeTab"
                      className="absolute inset-0 bg-white border-t-2 border-blue-500 shadow-sm"
                      initial={false}
                      transition={{
                        type: "spring",
                        stiffness: 500,
                        damping: 30,
                      }}
                    />
                  )}

                  {/* Tab content */}
                  <div className="relative z-10 flex items-center gap-2">
                    <motion.div
                      animate={{
                        scale: activeTab === tab.id ? 1.1 : 1,
                        color: activeTab === tab.id ? "#1d4ed8" : undefined,
                      }}
                      transition={{ duration: 0.2 }}
                    >
                      {tab.icon}
                    </motion.div>
                    <span className="hidden sm:inline">{tab.label}</span>
                  </div>

                  {/* Border separator */}
                  {index < tabs.length - 1 && (
                    <div className="absolute right-0 top-3 bottom-3 w-px bg-gray-200" />
                  )}
                </button>
              ))}
            </div>
          </div>

          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="p-3 border-b bg-white"
          >
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <p className="text-gray-600 leading-relaxed">
                  {tabs.find((tab) => tab.id === activeTab)?.description}
                </p>
              </div>
            </div>
          </motion.div>

          {activeTab === "point_to_point" && (
            <motion.div
              key="point_to_point_form"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{
                duration: 0.4,
                ease: "easeOut",
                delay: 0.1,
              }}
            >
              <OrderForm
                setFieldValue={setFieldValue}
                values={values}
                detailsRef={detailsRef}
                commoditiesRef={commoditiesRef}
                shipperCustomers={customersAddresses}
                consigneeCustomers={customersAddresses}
                billingCustomers={customers}
                accessorialsData={accessorialsData}
                orderId={orderId}
                touched={touched}
                errors={errors}
                refetchOrder={refetchOrder}
              />
            </motion.div>
          )}

          {activeTab === "single_shipper" && (
            <motion.div
              key="single_shipper_form"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{
                duration: 0.4,
                ease: "easeOut",
                delay: 0.1,
              }}
            >
              <SingleOrderForm
                setFieldValue={setFieldValue}
                setTouched={setTouched}
                values={values}
                detailsRef={detailsRef}
                commoditiesRef={commoditiesRef}
                accessorialsData={accessorialsData}
                title="Single Shipper"
                type="single_shipper"
              />
            </motion.div>
          )}

          {activeTab === "single_consignee" && (
            <motion.div
              key="single_consignee_form"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{
                duration: 0.4,
                ease: "easeOut",
                delay: 0.1,
              }}
            >
              <SingleOrderForm
                setFieldValue={setFieldValue}
                setTouched={setTouched}
                values={values}
                detailsRef={detailsRef}
                commoditiesRef={commoditiesRef}
                accessorialsData={accessorialsData}
                title="Single Consignee"
                type="single_consignee"
              />
            </motion.div>
          )}

          {activeTab === "sequence" && (
            <motion.div
              key="sequence_form"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{
                duration: 0.4,
                ease: "easeOut",
                delay: 0.1,
              }}
            >
              <SingleOrderForm
                setFieldValue={setFieldValue}
                setTouched={setTouched}
                values={values}
                detailsRef={detailsRef}
                commoditiesRef={commoditiesRef}
                accessorialsData={accessorialsData}
                title="Sequence"
                type="sequence"
              />
              {/* <SquenceForm
                setFieldValue={setFieldValue}
                setTouched={setTouched}
                values={values}
                detailsRef={detailsRef}
                commoditiesRef={commoditiesRef}
              /> */}
            </motion.div>
          )}
        </div>

        <Box
          sx={{
            width: "30%",
            position: "sticky",
            top: 163,
            zIndex: 999,
            height: "calc(100vh - 164px)",
            overflow: "auto",
          }}
        >
          <EventsTimeline events={order?.events} />
        </Box>
      </div>

      {/* Confirmation Modal */}
      <Dialog
        open={showConfirmModal}
        onClose={handleCancelTabSwitch}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className="bg-white/40 backdrop-blur-sm transition-all duration-200 ease-in-out"
      >
        <DialogTitle id="alert-dialog-title">
          Unsaved Changes Warning
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to switch tabs? Your data will be lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelTabSwitch} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmTabSwitch} color="error" autoFocus>
            Discard Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Billing Party Update Modal */}
      <BillingPartyUpdateModal
        open={showBillingPartyModal}
        onClose={() => setShowBillingPartyModal(false)}
        orderUuid={orderId}
        onSuccess={() => {
          refetchOrder();
          setShowBillingPartyModal(false);
        }}
      />

      {/* FIXED LOADING OVERLAY */}
      {(isLoading ||
        isFetching ||
        isLoadingTags ||
        isLoadingCustomers ||
        isLoadingAcc) && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-white/40 backdrop-blur-md transition-all duration-200 ease-in-out">
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: 4,
              borderRadius: 2,
              backgroundColor: "white",
              boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.15)",
            }}
          >
            <CircularProgress size={60} sx={{ color: "#1e55bf", mb: 2 }} />
            <Typography variant="h6" sx={{ mb: 1, color: "#333" }}>
              Loading Order Details
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", textAlign: "center" }}
            >
              Please wait while we fetch the order information
            </Typography>
          </Box>
        </div>
      )}
    </Box>
  );
};

export default CreateOrder;
